import { useEffect, useState } from "react";
import { adminListEbooks, adminCreateEbook, adminUpdateEbook, adminDeleteEbook } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import PdfUploader from "@/components/PdfUploader";
import EbookCategoryManager from "@/components/admin/EbookCategoryManager";
import { BookOpen, Plus, Trash2, Eye, EyeOff, Save, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  title: "", title_hi: "", description: "", description_hi: "", category: "",
  source: "", author: "", year: "", language: "", cover_url: "", pdf_url: "",
  tags: [], order: 0, hidden: false, featured: false,
};

const Field = ({ label, value, onChange, placeholder, testid, deva }) => (
  <div>
    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label}</label>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={`w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none ${deva ? "font-deva" : ""}`} />
  </div>
);

export default function AdminEbooks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, ...fields}
  const [catNames, setCatNames] = useState([]);

  const load = () => adminListEbooks().then((d) => setItems(d.items || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (b) => setEditing({ ...BLANK, ...b, tags: b.tags || [] });

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    const payload = { ...editing, order: Number(editing.order) || 0 };
    try {
      if (editing.id) { await adminUpdateEbook(editing.id, payload); toast("eBook updated"); }
      else { await adminCreateEbook(payload); toast("eBook created"); }
      setEditing(null);
      load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };

  const toggleHidden = async (b) => {
    try { await adminUpdateEbook(b.id, { hidden: !b.hidden }); load(); } catch (e) { toast.error(e.message); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    try { await adminDeleteEbook(b.id); toast("Deleted"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-ebooks">
      <div className="mb-2 flex items-center gap-2.5">
        <BookOpen className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">eBooks & Manuscripts</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-ink-3">
        Upload and organise PDF eBooks, scriptures and manuscripts. Visitors can read a preview; signed-in members can save and download. Run Sync (Preview → Live) to publish.
      </p>

      <EbookCategoryManager onChange={setCatNames} />

      {!editing && (
        <button onClick={startNew} data-testid="ebook-add-btn" className="mb-6 inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> Add eBook
        </button>
      )}

      {editing && (
        <div className="mb-8 space-y-4 border border-line bg-surface p-5" data-testid="ebook-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-serif-latin text-lg text-ink">{editing.id ? "Edit eBook" : "New eBook"}</h2>
            <button onClick={() => setEditing(null)} className="text-ink-3 hover:text-ink"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title (English)" value={editing.title} onChange={(v) => set("title", v)} testid="ebook-title" placeholder="Bhagavad Gita" />
            <Field label="Title (Hindi)" value={editing.title_hi} onChange={(v) => set("title_hi", v)} testid="ebook-title-hi" placeholder="श्रीमद्भगवद्गीता" deva />
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Category</label>
              <input list="ebook-cat-options" value={editing.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="Pick or type a category…" data-testid="ebook-category" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
              <datalist id="ebook-cat-options">
                {catNames.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <Field label="Author / Compiler" value={editing.author} onChange={(v) => set("author", v)} testid="ebook-author" placeholder="Vyasa (attrib.)" />
            <Field label="Year / Edition" value={editing.year} onChange={(v) => set("year", v)} testid="ebook-year" placeholder="1935" />
            <Field label="Language" value={editing.language} onChange={(v) => set("language", v)} testid="ebook-language" placeholder="Sanskrit / Hindi / English" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Description (English)</label>
            <textarea value={editing.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="ebook-description" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Description (Hindi)</label>
            <textarea value={editing.description_hi} onChange={(e) => set("description_hi", e.target.value)} rows={3} data-testid="ebook-description-hi" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none font-deva" />
          </div>
          <Field label="Source / Attribution" value={editing.source} onChange={(v) => set("source", v)} testid="ebook-source" placeholder="Gita Press, Gorakhpur (public domain)" />
          <Field label="Tags (comma separated)" value={(editing.tags || []).join(", ")} onChange={(v) => set("tags", v.split(",").map((t) => t.trim()).filter(Boolean))} testid="ebook-tags" placeholder="gita, krishna, vedanta" />
          <div className="grid gap-6 sm:grid-cols-2">
            <ImageUploader label="Cover image" testid="ebook-cover" value={editing.cover_url} onChange={(v) => set("cover_url", v)} />
            <PdfUploader label="PDF file" testid="ebook-pdf" value={editing.pdf_url} onChange={(v) => set("pdf_url", v)} />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="w-24">
              <Field label="Order" value={editing.order} onChange={(v) => set("order", v)} testid="ebook-order" placeholder="0" />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={!!editing.hidden} onChange={(e) => set("hidden", e.target.checked)} data-testid="ebook-hidden" /> Hidden</label>
            <label className="flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={!!editing.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="ebook-featured" /> Featured</label>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} data-testid="ebook-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white"><Save className="h-4 w-4" /> Save</button>
            {editing.id && <a href={`/books/${editing.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron"><ExternalLink className="h-4 w-4" /> View on site</a>}
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-16 text-center text-ink-3">…</p>
      ) : (
        <div className="space-y-3" data-testid="ebook-list">
          {items.map((b) => (
            <div key={b.id} className="flex items-center gap-4 border border-line bg-surface p-4" data-testid={`ebook-row-${b.slug}`}>
              <div className="h-16 w-12 shrink-0 overflow-hidden rounded border border-line bg-parchment">
                {b.cover_url ? <img src={b.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-ink-3"><BookOpen className="h-5 w-5" /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif-latin text-ink">{b.title} {b.hidden && <span className="ml-1 rounded bg-ink/10 px-1.5 py-0.5 text-[10px] uppercase text-ink-3">Hidden</span>}</p>
                <p className="truncate text-xs text-ink-3">{[b.category, b.author, b.pdf_url ? "PDF ✓" : "no PDF"].filter(Boolean).join(" · ")}</p>
              </div>
              <button onClick={() => toggleHidden(b)} title={b.hidden ? "Show" : "Hide"} className="text-ink-3 hover:text-saffron" data-testid={`ebook-toggle-${b.slug}`}>{b.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              <button onClick={() => startEdit(b)} className="text-sm text-saffron hover:underline" data-testid={`ebook-edit-${b.slug}`}>Edit</button>
              <button onClick={() => remove(b)} title="Delete" className="text-ink-3 hover:text-red-600" data-testid={`ebook-delete-${b.slug}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {items.length === 0 && <p className="py-10 text-center text-ink-3">No eBooks yet. Click “Add eBook” to upload your first PDF.</p>}
        </div>
      )}
    </div>
  );
}
